import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Play, Pause } from 'lucide-react';
import { CircuitPaths, CharReveal, Reveal } from './TerminalEffects';

const API = process.env.REACT_APP_BACKEND_URL;
const BAR_COUNT = 48;

const AudioPlayer = ({ src, label, subtitle, testId }) => {
  const audioRef = useRef(null);
  const animRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [bars, setBars] = useState(() => Array(BAR_COUNT).fill(0.15));

  const animate = useCallback(() => {
    if (!audioRef.current) return;
    const t = audioRef.current.currentTime;
    const d = audioRef.current.duration || 1;
    setProgress(t / d);

    if (playing) {
      setBars(prev => prev.map((_, i) => {
        const base = 0.15;
        const wave = Math.sin(t * 3 + i * 0.4) * 0.3;
        const pulse = Math.sin(t * 1.5 + i * 0.2) * 0.15;
        const rand = (Math.sin(t * 7 + i * 1.7) + 1) * 0.1;
        return Math.max(base, Math.min(1, base + wave + pulse + rand));
      }));
    }
    animRef.current = requestAnimationFrame(animate);
  }, [playing]);

  useEffect(() => {
    if (playing) {
      animRef.current = requestAnimationFrame(animate);
    } else {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      setBars(prev => prev.map(() => 0.15));
    }
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [playing, animate]);

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.play().catch(() => {});
      setPlaying(true);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    setProgress(0);
  };

  return (
    <div className="space-y-4" data-testid={testId}>
      <audio ref={audioRef} src={src} preload="metadata" onEnded={handleEnded} />

      {/* Label */}
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-2 h-2 rounded-full ${playing ? 'bg-[#00FFD1] shadow-[0_0_8px_rgba(0,255,209,0.6)]' : 'bg-white/20'}`} />
        <span className="text-sm font-semibold text-[#F5F0E0]/80 uppercase tracking-[0.15em]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {label}
        </span>
        <span className="text-xs text-[#C4956A]/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {subtitle}
        </span>
      </div>

      {/* Waveform + play button */}
      <div className="w-full flex items-center gap-5 sm:gap-6">
        <button
          onClick={togglePlay}
          className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all duration-300 group ${
            playing
              ? 'bg-[#00FFD1] shadow-[0_0_40px_rgba(0,255,209,0.35)]'
              : 'bg-[#00FFD1]/10 border-2 border-[#00FFD1]/40 hover:bg-[#00FFD1]/20 hover:border-[#00FFD1]/60 hover:shadow-[0_0_30px_rgba(0,255,209,0.15)]'
          }`}
          data-testid={`${testId}-play-btn`}
        >
          {playing ? (
            <Pause className="h-5 w-5 sm:h-6 sm:w-6 text-[#2A2A2A]" />
          ) : (
            <Play className="h-5 w-5 sm:h-6 sm:w-6 text-[#00FFD1] group-hover:text-[#7DFFD4] ml-0.5" />
          )}
        </button>

        {/* Soundwave bars */}
        <div className="flex-1 flex items-center justify-center gap-[2px] sm:gap-[3px] h-14 sm:h-16">
          {bars.map((h, i) => {
            const isPlayed = i / BAR_COUNT < progress;
            return (
              <div
                key={`waveform-bar-${i}`}
                className="flex-1 max-w-[6px] rounded-full transition-colors duration-150"
                style={{
                  height: `${Math.max(12, h * 100)}%`,
                  backgroundColor: isPlayed ? '#00FFD1' : 'rgba(255,255,255,0.12)',
                  transition: playing ? 'height 120ms ease, background-color 150ms' : 'height 400ms ease, background-color 300ms',
                }}
              />
            );
          })}
        </div>
      </div>

      {/* Progress bar */}
      <div className="w-full h-0.5 bg-white/5 rounded-full overflow-hidden">
        <div
          className="h-full bg-[#00FFD1]/50 rounded-full transition-all duration-200"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
    </div>
  );
};

export const AudioDemoSection = () => {
  return (
    <section className="relative py-20 sm:py-28 bg-[#2A2A2A] overflow-hidden" data-testid="audio-demo-section">
      {/* subtle glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full bg-[#00FFD1]/[0.06] blur-[120px] pointer-events-none" />
      <CircuitPaths />

      <div className="relative z-10 max-w-[800px] mx-auto px-6">
        <Reveal>
          <div className="text-center mb-14">
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C4956A] mb-4">
              Hear It In Action
            </p>
            <CharReveal text="Meet Your AI Receptionist" tag="h2"
              className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#F5F0E0] leading-[1.1]" />
          </div>
        </Reveal>

        <Reveal delay={100}>
          <div className="space-y-10">
            <AudioPlayer
              src={`${API}/api/static/demo_british_female.mp3`}
              label="Charlotte"
              subtitle="British Female"
              testId="audio-demo-female"
            />

            <div className="w-full h-px bg-white/5" />

            <AudioPlayer
              src={`${API}/api/static/demo_british_male.mp3`}
              label="James"
              subtitle="American Male"
              testId="audio-demo-male"
            />
          </div>
        </Reveal>

        <Reveal delay={200}>
          <p className="text-sm text-[#F5F0E0]/40 text-center max-w-md mx-auto leading-relaxed mt-10" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Press play to hear a demo of your AI receptionist in action
          </p>
        </Reveal>
      </div>
    </section>
  );
};
